import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  LifeBuoy, Plus, Pencil, Trash2, X, Table2, Bell, PoundSterling, ChevronRight,
  AlertTriangle, Webhook, RefreshCw, Copy, Zap,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Pager, usePaged } from "../components/Pager";
import AssignSiteButton from "../components/AssignSiteButton";
import MarginTrend from "../components/MarginTrend";
import { fmtDateTimeGB, fmtDateGB } from "../lib/format";
import DateInput from "../components/DateInput";

const fmtGbp = (v) => v == null ? "—" : `£${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const STATUSES = [
  { k: "open", l: "Open", c: "#2563EB" },
  { k: "in_progress", l: "In Progress", c: "#B45309" },
  { k: "waiting", l: "Waiting", c: "#7C3AED" },
  { k: "resolved", l: "Resolved", c: "#15934F" },
  { k: "closed", l: "Closed", c: "#64748B" },
];
const SMAP = Object.fromEntries(STATUSES.map((s) => [s.k, s]));
function TicketsTable({ tickets, priorityFilter, render }) {
  const shown = priorityFilter ? tickets.filter((t) => t.priority === priorityFilter) : tickets;
  const pager = usePaged(shown, "eneprox.opex.perPage", `${priorityFilter}|${shown.length}`);
  return render(shown, pager);
}

const PRIORITIES = [
  { k: "low", l: "Low", c: "#64748B" },
  { k: "medium", l: "Medium", c: "#2563EB" },
  { k: "high", l: "High", c: "#B45309" },
  { k: "critical", l: "Critical", c: "#C0392B" },
];
const PMAP = Object.fromEntries(PRIORITIES.map((p) => [p.k, p]));
const CATEGORIES = ["visit", "repair", "maintenance", "alert", "other"];
const SEV = { info: "#64748B", warning: "#B45309", critical: "#C0392B" };

/* ── Ticket modal ────────────────────────────────────────────────────────── */
function TicketModal({ open, onClose, onSaved, projects, users, existing }) {
  const blank = { title: "", description: "", category: "other", priority: "medium", status: "open", project_id: "", assignee_id: "", due_date: "", cost_gbp: "" };
  const [f, setF] = useState(blank);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setF(existing ? {
      title: existing.title || "", description: existing.description || "",
      category: existing.category || "other", priority: existing.priority || "medium",
      status: existing.status || "open", project_id: existing.project_id || "",
      assignee_id: existing.assignee_id || "", due_date: existing.due_date || "",
      cost_gbp: existing.cost_gbp ?? "",
    } : blank);
  }, [open, existing]); // eslint-disable-line
  if (!open) return null;
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!f.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    const body = {
      title: f.title, description: f.description || null, category: f.category,
      priority: f.priority, status: f.status, project_id: f.project_id || null,
      assignee_id: f.assignee_id || null, due_date: f.due_date || null,
      cost_gbp: f.cost_gbp === "" ? null : Number(f.cost_gbp),
    };
    try {
      if (existing) await api.patch(`/opex/tickets/${existing.id}`, body);
      else await api.post(`/opex/tickets`, body);
      toast.success(existing ? "Ticket updated" : "Ticket created");
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="ticket-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--line)] sticky top-0 bg-white">
          <h2 className="font-display text-lg font-bold flex items-center gap-2"><LifeBuoy size={18} className="text-[var(--accent)]" /> {existing ? "Edit ticket" : "New ticket"}</h2>
          <button onClick={onClose} className="btn-ghost p-1" data-testid="ticket-modal-close"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div><label className="label">Title</label>
            <input className="input-swiss mt-1" value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="ticket-title" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">Category</label>
              <select className="input-swiss mt-1" value={f.category} onChange={(e) => set("category", e.target.value)} data-testid="ticket-category">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c[0].toUpperCase() + c.slice(1)}</option>)}
              </select></div>
            <div><label className="label">Priority</label>
              <select className="input-swiss mt-1" value={f.priority} onChange={(e) => set("priority", e.target.value)} data-testid="ticket-priority">
                {PRIORITIES.map((p) => <option key={p.k} value={p.k}>{p.l}</option>)}
              </select></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">Status</label>
              <select className="input-swiss mt-1" value={f.status} onChange={(e) => set("status", e.target.value)} data-testid="ticket-status">
                {STATUSES.map((s) => <option key={s.k} value={s.k}>{s.l}</option>)}
              </select></div>
            <div><label className="label">Due date</label>
              <DateInput className="input-swiss mt-1" value={f.due_date} onChange={(e) => set("due_date", e.target.value)} data-testid="ticket-due" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">Site</label>
              <select className="input-swiss mt-1" value={f.project_id} onChange={(e) => set("project_id", e.target.value)} data-testid="ticket-project">
                <option value="">— Unassigned —</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select></div>
            <div><label className="label">Assignee</label>
              <select className="input-swiss mt-1" value={f.assignee_id} onChange={(e) => set("assignee_id", e.target.value)} data-testid="ticket-assignee">
                <option value="">— Unassigned —</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
              </select></div>
          </div>
          <div><label className="label">Cost (£)</label>
            <input type="number" step="0.01" className="input-swiss mt-1" value={f.cost_gbp} onChange={(e) => set("cost_gbp", e.target.value)} placeholder="Expenditure for this ticket" data-testid="ticket-cost" /></div>
          <div><label className="label">Description</label>
            <textarea className="input-swiss mt-1" rows={3} value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="ticket-description" /></div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-[var(--line)] sticky bottom-0 bg-white">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={submit} disabled={saving} className="btn-primary" data-testid="ticket-save">{saving ? "Saving…" : existing ? "Save" : "Create"}</button>
        </div>
      </div>
    </div>
  );
}

/* ── Alert manual modal ──────────────────────────────────────────────────── */
function AlertModal({ open, onClose, onSaved, projects }) {
  const blank = { message: "", severity: "warning", source_platform: "", device: "", code: "", project_id: "" };
  const [f, setF] = useState(blank);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF(blank); }, [open]); // eslint-disable-line
  if (!open) return null;
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!f.message.trim()) { toast.error("Message is required"); return; }
    setSaving(true);
    try {
      await api.post(`/opex/alerts`, { ...f, project_id: f.project_id || null, cost_gbp: undefined });
      toast.success("Alert logged — ticket created");
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="alert-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--line)]">
          <h2 className="font-display text-lg font-bold flex items-center gap-2"><Bell size={18} className="text-[var(--accent)]" /> Log alert</h2>
          <button onClick={onClose} className="btn-ghost p-1"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div><label className="label">Message</label>
            <input className="input-swiss mt-1" value={f.message} onChange={(e) => set("message", e.target.value)} placeholder="e.g. Inverter 2 offline" data-testid="alert-message" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">Severity</label>
              <select className="input-swiss mt-1" value={f.severity} onChange={(e) => set("severity", e.target.value)} data-testid="alert-severity">
                <option value="info">Info</option><option value="warning">Warning</option><option value="critical">Critical</option>
              </select></div>
            <div><label className="label">Platform</label>
              <input className="input-swiss mt-1" value={f.source_platform} onChange={(e) => set("source_platform", e.target.value)} placeholder="SolarEdge" data-testid="alert-platform" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">Device</label>
              <input className="input-swiss mt-1" value={f.device} onChange={(e) => set("device", e.target.value)} placeholder="INV-2" /></div>
            <div><label className="label">Fault code</label>
              <input className="input-swiss mt-1" value={f.code} onChange={(e) => set("code", e.target.value)} placeholder="E013" /></div>
          </div>
          <div><label className="label">Site</label>
            <select className="input-swiss mt-1" value={f.project_id} onChange={(e) => set("project_id", e.target.value)} data-testid="alert-project">
              <option value="">— Unassigned —</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select></div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-[var(--line)]">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={submit} disabled={saving} className="btn-primary" data-testid="alert-save">{saving ? "Saving…" : "Log alert"}</button>
        </div>
      </div>
    </div>
  );
}

/* ── Page ────────────────────────────────────────────────────────────────── */
export default function Opex() {
  const [searchParams] = useSearchParams();
  const [view, setView] = useState(searchParams.get("view") || "tickets");
  const [summary, setSummary] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [expend, setExpend] = useState(null);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "");
  const [priorityFilter, setPriorityFilter] = useState(searchParams.get("priority") || "");
  const [loading, setLoading] = useState(true);
  const [tModal, setTModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [aModal, setAModal] = useState(false);
  const [webhook, setWebhook] = useState(null);
  const [range, setRange] = useState({ from: "", to: "" });
  const [drift, setDrift] = useState(null);

  const assignTicket = async (t, pid) => {
    try {
      await api.patch(`/opex/tickets/${t.id}`, { project_id: pid });
      toast.success(`Ticket linked to ${projects.find((p) => p.id === pid)?.name || "site"}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Assign failed"); throw e; }
  };
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t, a] = await Promise.all([
        api.get(`/opex/summary`),
        api.get(`/opex/tickets${statusFilter ? `?status=${statusFilter}` : ""}`),
        api.get(`/opex/alerts`),
      ]);
      setSummary(s.data); setTickets(t.data.tickets || []); setAlerts(a.data.alerts || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to load OPEX"); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get(`/projects`).then(({ data }) => setProjects((data || []).map((p) => ({ id: p.id, name: p.name })))).catch(() => {});
    api.get(`/users`).then(({ data }) => setUsers((data || []).map((u) => ({ id: u.id, name: u.name, email: u.email })))).catch(() => {});
  }, []);

  const loadExpend = useCallback(async () => {
    const qs = [];
    if (range.from) qs.push(`date_from=${range.from}`);
    if (range.to) qs.push(`date_to=${range.to}`);
    const { data } = await api.get(`/opex/expenditure${qs.length ? `?${qs.join("&")}` : ""}`);
    setExpend(data);
  }, [range]);
  useEffect(() => { if (view === "expenditure") loadExpend(); }, [view, loadExpend]);

  const setTicketStatus = async (t, status) => {
    setTickets((l) => l.map((x) => x.id === t.id ? { ...x, status } : x));
    try { await api.patch(`/opex/tickets/${t.id}`, { status }); load(); }
    catch (e) { toast.error("Update failed"); load(); }
  };
  const delTicket = async (t) => { if (!window.confirm("Delete this ticket?")) return; try { await api.delete(`/opex/tickets/${t.id}`); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); } };
  const dismissAlert = async (a) => { try { await api.post(`/opex/alerts/${a.id}/dismiss`); toast.success("Alert dismissed"); load(); } catch { toast.error("Failed"); } };
  const loadWebhook = async () => { try { const { data } = await api.get(`/opex/webhook-info`); setWebhook(data); } catch { toast.error("Admin only"); } };
  const rotateWebhook = async () => { try { const { data } = await api.post(`/opex/webhook-token/rotate`); setWebhook((w) => ({ ...w, token: data.token, curl: (w?.curl || "").replace(/X-Webhook-Token: \S+/, `X-Webhook-Token: ${data.token}`) })); toast.success("Token rotated"); } catch { toast.error("Failed"); } };
  const scanPerf = async () => { try { const { data } = await api.post(`/opex/alerts/scan-performance`); toast.success(`Scanned ${data.scanned} sites · ${data.alerts_created} new alerts`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Scan failed"); } };
  const loadDrift = async () => { try { const { data } = await api.get(`/opex/drift-config`); setDrift(data); } catch {} };
  const saveDrift = async (patch) => { try { const { data } = await api.post(`/opex/drift-config`, { ...drift, ...patch }); setDrift(data); toast.success("Drift settings saved"); } catch { toast.error("Save failed"); } };
  const scanDrift = async () => { try { const { data } = await api.post(`/opex/alerts/scan-drift`); toast.success(`Drift scan: ${data.scanned} sites · ${data.alerts_created} new alerts`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Scan failed"); } };
  useEffect(() => { if (view === "alerts" && !drift) loadDrift(); }, [view]); // eslint-disable-line

  const stats = summary ? [
    { l: "Open tickets", v: summary.open_count, c: "#2563EB" },
    { l: "Critical open", v: summary.critical_open_count, c: "#C0392B" },
    { l: "New alerts", v: summary.new_alert_count, c: "#B45309" },
    { l: "Ticket spend", v: fmtGbp(summary.ticket_cost_total_gbp), c: "#15934F" },
  ] : [];

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="opex-page">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="label mb-2">Execution</div>
          <h1 className="font-display text-4xl font-bold">OPEX hub</h1>
          <p className="muted mt-2 text-sm max-w-2xl">
            Centralise helpdesk tickets and inverter/manufacturer alerts, action maintenance, and track all operational
            expenditure once a site enters operation.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setAModal(true)} className="btn-ghost text-xs" data-testid="opex-log-alert"><Bell size={13} /> Log alert</button>
          <button onClick={() => { setEditing(null); setTModal(true); }} className="btn-primary" data-testid="opex-new-ticket"><Plus size={15} /> New ticket</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {stats.map((s) => (
          <div key={s.l} className="swiss-card-flat p-4" data-testid={`opex-stat-${s.l}`}>
            <div className="text-[10px] uppercase tracking-[0.12em] font-bold muted">{s.l}</div>
            <div className="font-display text-2xl font-bold mt-1" style={{ color: s.c }}>{s.v}</div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div className="inline-flex rounded border border-[var(--line)] overflow-hidden text-xs font-semibold" data-testid="opex-view-toggle">
          {[
            { k: "tickets", l: "Tickets", icon: Table2 },
            { k: "alerts", l: "Alerts", icon: Bell },
            { k: "expenditure", l: "Expenditure", icon: PoundSterling },
          ].map((v) => (
            <button key={v.k} onClick={() => setView(v.k)} className={`px-3.5 py-1.5 inline-flex items-center gap-1.5 transition-colors ${view === v.k ? "bg-[var(--ink)] text-white" : "bg-white hover:bg-[var(--bg-2)]"}`} data-testid={`opex-view-${v.k}`}>
              <v.icon size={13} /> {v.l}{v.k === "alerts" && summary?.new_alert_count ? ` (${summary.new_alert_count})` : ""}
            </button>
          ))}
        </div>
        {view === "tickets" && (
          <div className="flex items-center gap-2">
            <select className="input-swiss py-1.5 text-xs w-auto" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="opex-status-filter">
              <option value="">All statuses</option>
              {STATUSES.map((s) => <option key={s.k} value={s.k}>{s.l}</option>)}
            </select>
            <select className="input-swiss py-1.5 text-xs w-auto" value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} data-testid="opex-priority-filter">
              <option value="">All priorities</option>
              {PRIORITIES.map((p) => <option key={p.k} value={p.k}>{p.l}</option>)}
            </select>
          </div>
        )}
      </div>

      {loading && view !== "expenditure" ? (
        <div className="muted text-sm">Loading…</div>
      ) : view === "tickets" ? (
        <TicketsTable tickets={tickets} priorityFilter={priorityFilter} render={(shown, pager) => shown.length === 0 ? <div className="muted text-sm p-6 swiss-card-flat">No tickets.</div> : (
          <div className="swiss-card-flat">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="opex-tickets-table">
              <thead><tr className="text-left label border-b border-[var(--line)] bg-[var(--surface-2)]">
                <th className="py-3 px-4">Title</th><th className="py-3 px-4">Category</th><th className="py-3 px-4">Priority</th>
                <th className="py-3 px-4">Site</th><th className="py-3 px-4">Assignee</th><th className="py-3 px-4">Due</th>
                <th className="py-3 px-4 text-right">Cost</th><th className="py-3 px-4">Status</th><th className="py-3 px-4 text-right">Actions</th>
              </tr></thead>
              <tbody>
                {pager.rows.map((t) => {
                  const p = PMAP[t.priority] || PMAP.medium;
                  return (
                    <tr key={t.id} className="border-b border-[var(--line)] hover:bg-[var(--bg-2)]" data-testid={`opex-ticket-${t.id}`}>
                      <td className="py-2 px-4 font-medium max-w-[240px] truncate" title={t.title}>{t.title}{t.alert_id && <Bell size={11} className="inline ml-1.5 text-[var(--ink-2)]" />}</td>
                      <td className="py-2 px-4 capitalize">{t.category}</td>
                      <td className="py-2 px-4"><span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: p.c + "22", color: p.c }}>{p.l}</span></td>
                      <td className="py-2 px-4">{t.project_name || <span className="muted">—</span>}</td>
                      <td className="py-2 px-4">{t.assignee_name || <span className="muted">—</span>}</td>
                      <td className="py-2 px-4 font-mono text-xs">{fmtDateGB(t.due_date)}</td>
                      <td className="py-2 px-4 text-right font-mono">{t.cost_gbp ? fmtGbp(t.cost_gbp) : "—"}</td>
                      <td className="py-2 px-4">
                        <select value={t.status} onChange={(e) => setTicketStatus(t, e.target.value)} className="text-[10px] font-bold rounded px-1.5 py-1 border-0 cursor-pointer" style={{ background: (SMAP[t.status]?.c || "#64748B") + "22", color: SMAP[t.status]?.c || "#64748B" }} data-testid={`opex-ticket-status-${t.id}`}>
                          {STATUSES.map((s) => <option key={s.k} value={s.k}>{s.l}</option>)}
                        </select>
                      </td>
                      <td className="py-2 px-4 text-right whitespace-nowrap">
                        <div className="inline-flex items-center gap-1.5">
                          <button onClick={() => delTicket(t)} className="btn-ghost !px-2.5 text-[#C0392B]" data-testid={`opex-ticket-del-${t.id}`} title="Delete" aria-label="Delete ticket"><Trash2 size={13} /></button>
                          <button onClick={() => { setEditing(t); setTModal(true); }} className="btn-ghost !px-2.5" data-testid={`opex-ticket-edit-${t.id}`} title="Edit" aria-label="Edit ticket"><Pencil size={13} /></button>
                          {t.project_id
                            ? <Link to={`/projects/${t.project_id}?tab=OPEX`} className="btn-ghost" data-testid={`opex-ticket-open-${t.id}`}>Open <ChevronRight size={13} /></Link>
                            : <AssignSiteButton projects={projects} onAssign={(pid) => assignTicket(t, pid)} testid={`opex-ticket-assign-${t.id}`} />}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pager {...pager.pagerProps} testid="opex-tickets-pager" />
          </div>
        )} />
      ) : view === "alerts" ? (
        <div className="space-y-4" data-testid="opex-alerts">
          <div className="swiss-card-flat p-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="label flex items-center gap-2"><Webhook size={13} /> Alert ingestion</div>
              <div className="flex items-center gap-2">
                <button onClick={scanPerf} className="btn-ghost text-xs" data-testid="opex-scan-perf"><Zap size={13} /> Scan performance</button>
                <button onClick={loadWebhook} className="btn-ghost text-xs" data-testid="opex-webhook-show"><Webhook size={13} /> Webhook URL</button>
              </div>
            </div>
            {webhook && (
              <div className="mt-3 space-y-2" data-testid="opex-webhook-box">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="label text-[10px] shrink-0">POST URL</span>
                  <code className="text-[11px] bg-[var(--bg-2)] px-2 py-1 rounded border border-[var(--line)] break-all flex-1 min-w-0">{webhook.url}</code>
                  <button onClick={() => { navigator.clipboard?.writeText(webhook.url); toast.success("URL copied"); }} className="btn-ghost p-1.5" title="Copy URL"><Copy size={13} /></button>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="label text-[10px] shrink-0">Header</span>
                  <code className="text-[11px] bg-[var(--bg-2)] px-2 py-1 rounded border border-[var(--line)] break-all flex-1 min-w-0">{webhook.header_name}: {webhook.token}</code>
                  <button onClick={() => { navigator.clipboard?.writeText(`${webhook.header_name}: ${webhook.token}`); toast.success("Header copied"); }} className="btn-ghost p-1.5" title="Copy header"><Copy size={13} /></button>
                  <button onClick={rotateWebhook} className="btn-ghost text-xs" data-testid="opex-webhook-rotate"><RefreshCw size={12} /> Rotate</button>
                </div>
                {webhook.curl && (
                  <div className="flex items-center gap-2">
                    <code className="text-[10px] bg-[var(--bg-2)] px-2 py-1 rounded border border-[var(--line)] break-all flex-1 min-w-0">{webhook.curl}</code>
                    <button onClick={() => { navigator.clipboard?.writeText(webhook.curl); toast.success("Example copied"); }} className="btn-ghost p-1.5" title="Copy curl"><Copy size={13} /></button>
                  </div>
                )}
              </div>
            )}
            <p className="text-[11px] muted mt-2">POST JSON alerts from inverter/manufacturer platforms to this URL. Send the token in the <code>X-Webhook-Token</code> header (kept out of URLs/logs). Each becomes an alert and auto-spawns a ticket.</p>
            <div className="mt-3 pt-3 border-t border-[var(--line)]" data-testid="opex-drift-config">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="label">Meter drift auto-alert</div>
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input type="checkbox" checked={!!drift?.meter_drift_active} onChange={(e) => saveDrift({ meter_drift_active: e.target.checked })} data-testid="drift-active" /> Enabled
                </label>
              </div>
              <div className="flex items-end gap-3 flex-wrap mt-2">
                <label className="text-xs">Threshold (±%)
                  <input type="number" className="input-swiss mt-1 w-24 block" value={drift?.meter_drift_threshold_pct ?? 10} onChange={(e) => setDrift((d) => ({ ...d, meter_drift_threshold_pct: Number(e.target.value) }))} onBlur={(e) => saveDrift({ meter_drift_threshold_pct: Number(e.target.value) })} data-testid="drift-threshold" /></label>
                <label className="text-xs">Over (days)
                  <input type="number" className="input-swiss mt-1 w-24 block" value={drift?.meter_drift_days ?? 3} onChange={(e) => setDrift((d) => ({ ...d, meter_drift_days: Number(e.target.value) }))} onBlur={(e) => saveDrift({ meter_drift_days: Number(e.target.value) })} data-testid="drift-days" /></label>
                <button onClick={scanDrift} className="btn-ghost text-xs" data-testid="opex-scan-drift"><Zap size={13} /> Scan drift now</button>
              </div>
              <p className="text-[11px] muted mt-2">Auto-raises an OPEX alert when a site's generation meter differs from its inverter by more than the threshold on each of the last N days.</p>
            </div>
          </div>

          {alerts.length === 0 ? <div className="muted text-sm p-6 swiss-card-flat">No alerts received.</div> : (
            <div className="space-y-2">
              {alerts.map((a) => (
                <div key={a.id} className={`swiss-card-flat p-3 flex items-start gap-3 ${a.status === "dismissed" ? "opacity-50" : ""}`} data-testid={`opex-alert-${a.id}`}>
                  <span className="mt-0.5 w-2.5 h-2.5 rounded-full shrink-0" style={{ background: SEV[a.severity] || "#64748B" }} />
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-medium">{a.message}</div>
                    <div className="text-[10px] muted mt-0.5">
                      {a.source_platform || a.source} · {a.project_name || "Unassigned"}{a.device ? ` · ${a.device}` : ""}{a.code ? ` · ${a.code}` : ""} · {fmtDateTimeGB(a.received_at)}
                    </div>
                  </div>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0" style={{ background: SEV[a.severity] + "22", color: SEV[a.severity] }}>{a.severity}</span>
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 bg-[var(--bg-2)] muted">{a.status}</span>
                  {a.status !== "dismissed" && <button onClick={() => dismissAlert(a)} className="btn-ghost p-1 text-[#C0392B] shrink-0" title="Dismiss" data-testid={`opex-alert-dismiss-${a.id}`}><X size={13} /></button>}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-5" data-testid="opex-expenditure">
          <div className="flex items-end gap-3 flex-wrap swiss-card-flat p-4">
            <label className="text-xs">From<DateInput className="input-swiss mt-1 block" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} data-testid="opex-exp-from" /></label>
            <label className="text-xs">To<DateInput className="input-swiss mt-1 block" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} data-testid="opex-exp-to" /></label>
            <button onClick={loadExpend} className="btn-ghost text-xs" data-testid="opex-exp-apply">Apply</button>
            {(range.from || range.to) && <button onClick={() => setRange({ from: "", to: "" })} className="text-xs muted hover:text-[var(--ink)]">Clear</button>}
          </div>
          {expend && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="swiss-card-flat p-4"><div className="text-[10px] uppercase tracking-[0.12em] font-bold muted">Ticket spend</div><div className="font-display text-2xl font-bold mt-1 text-[#2563EB]">{fmtGbp(expend.total_ticket_spend_gbp)}</div></div>
                <div className="swiss-card-flat p-4"><div className="text-[10px] uppercase tracking-[0.12em] font-bold muted">Maintenance spend</div><div className="font-display text-2xl font-bold mt-1 text-[#B45309]">{fmtGbp(expend.total_maintenance_spend_gbp)}</div></div>
                <div className="swiss-card-flat p-4"><div className="text-[10px] uppercase tracking-[0.12em] font-bold muted">Recorded income</div><div className="font-display text-2xl font-bold mt-1 text-[#15934F]">{fmtGbp(expend.total_income_gbp)}</div></div>
                <div className="rounded-lg border p-4" style={{ borderColor: (expend.total_net_margin_gbp >= 0 ? "#15934F" : "#C0392B") + "55", background: (expend.total_net_margin_gbp >= 0 ? "#15934F" : "#C0392B") + "11" }}><div className="text-[10px] uppercase tracking-[0.12em] font-bold" style={{ color: expend.total_net_margin_gbp >= 0 ? "#15934F" : "#C0392B" }}>Net operating margin</div><div className="font-display text-2xl font-bold mt-1" style={{ color: expend.total_net_margin_gbp >= 0 ? "#15934F" : "#C0392B" }}>{fmtGbp(expend.total_net_margin_gbp)}</div></div>
              </div>
              {expend.sites.length === 0 ? <div className="muted text-sm p-6 swiss-card-flat">No spend recorded in this period.</div> : (
                <div className="overflow-x-auto swiss-card-flat">
                  <table className="w-full text-sm" data-testid="opex-exp-table">
                    <thead><tr className="text-left label border-b border-[var(--line)] bg-[var(--surface-2)]">
                      <th className="py-3 px-4">Site</th><th className="py-3 px-4 text-right">Ticket £</th>
                      <th className="py-3 px-4 text-right">Maintenance £</th><th className="py-3 px-4 text-right">Total spend</th>
                      <th className="py-3 px-4 text-right">Income £</th><th className="py-3 px-4 text-right">Net margin</th>
                    </tr></thead>
                    <tbody>
                      {expend.sites.map((s) => (
                        <tr key={s.project_id || "_un"} className="border-b border-[var(--line)] hover:bg-[var(--bg-2)]" data-testid={`opex-exp-row-${s.project_id || "unassigned"}`}>
                          <td className="py-2 px-4 font-medium">{s.project_name}</td>
                          <td className="py-2 px-4 text-right font-mono">{fmtGbp(s.ticket_spend_gbp)}</td>
                          <td className="py-2 px-4 text-right font-mono">{fmtGbp(s.maintenance_spend_gbp)}</td>
                          <td className="py-2 px-4 text-right font-mono">{fmtGbp(s.total_spend_gbp)}</td>
                          <td className="py-2 px-4 text-right font-mono text-[#15934F]">{fmtGbp(s.income_gbp)}</td>
                          <td className="py-2 px-4 text-right font-mono font-bold" style={{ color: s.net_margin_gbp >= 0 ? "#15934F" : "#C0392B" }}>{fmtGbp(s.net_margin_gbp)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <MarginTrend months={12} title="Portfolio net operating margin trend" />
            </>
          )}
        </div>
      )}

      <TicketModal open={tModal} onClose={() => setTModal(false)} onSaved={load} projects={projects} users={users} existing={editing} />
      <AlertModal open={aModal} onClose={() => setAModal(false)} onSaved={load} projects={projects} />
    </div>
  );
}
